<template>
  <div
    v-clickoutside="close"
    class="sidebar-user-menu"
    @keydown.esc="closeAndFocusTrigger"
  >
    <button
      ref="trigger"
      type="button"
      class="sidebar-user-menu__trigger"
      :class="{ 'is-open': isOpen }"
      :title="collapsed ? name : null"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="isOpen ? panelId : null"
      @click="toggle"
    >
      <user-avatar :name="name" aria-hidden="true" />
      <span class="sidebar-user-menu__identity">
        <span class="sidebar-user-menu__first-name">{{ firstName }}</span>
        <span class="sidebar-user-menu__profile">{{ profileLabel }}</span>
      </span>
      <lucide-icon
        :icon="icons.chevronUp"
        :size="16"
        class="sidebar-user-menu__chevron"
        aria-hidden="true"
      />
    </button>

    <transition name="sidebar-user-menu-panel">
      <div
        v-if="isOpen"
        :id="panelId"
        role="region"
        :aria-label="$t('menuusuario')"
        class="sidebar-user-menu__panel"
        :style="panelStyle"
      >
        <div class="sidebar-user-menu__header">
          <p class="sidebar-user-menu__name">{{ name }}</p>
          <p class="sidebar-user-menu__header-profile">{{ profileLabel }}</p>
        </div>

        <div class="sidebar-user-menu__actions">
          <button
            type="button"
            class="sidebar-user-menu__option"
            @click="openLanguageModal"
          >
            <lucide-icon
              :icon="icons.globe"
              :size="18"
              class="sidebar-user-menu__option-icon"
              aria-hidden="true"
            />
            <span class="sidebar-user-menu__option-label">{{
              $t('idioma')
            }}</span>
            <span class="sidebar-user-menu__option-value">{{
              currentLocale
            }}</span>
            <lucide-icon
              :icon="icons.chevronRight"
              :size="16"
              class="sidebar-user-menu__option-chevron"
              aria-hidden="true"
            />
          </button>

          <button
            type="button"
            class="dm-button dm-button--danger dm-button--block"
            @click="handleLogout"
          >
            <lucide-icon :icon="icons.logOut" :size="16" aria-hidden="true" />
            {{ $t('sair') }}
          </button>
        </div>
      </div>
    </transition>

    <language-settings-modal
      :open.sync="isLanguageModalOpen"
      :current-locale="currentLocale"
      :options="availableLocales"
      @confirm="changeLocale"
    />
  </div>
</template>

<script>
import { ChevronRight, ChevronUp, Globe, LogOut } from 'lucide'
import Clickoutside from 'element-ui/lib/utils/clickoutside'
import LucideIcon from '@/components/LucideIcon'
import UserMenu from '@/layout/mixin/UserMenu'
import UserAvatar from './UserAvatar'
import LanguageSettingsModal from './LanguageSettingsModal'

const PANEL_OFFSET = 8

export default {
  name: 'SidebarUserMenu',
  components: { LucideIcon, UserAvatar, LanguageSettingsModal },
  directives: { Clickoutside },
  mixins: [UserMenu],
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      isLanguageModalOpen: false,
      panelStyle: {}
    }
  },
  computed: {
    panelId() {
      return `sidebar-user-menu-${this._uid}`
    },
    icons() {
      return {
        chevronRight: ChevronRight,
        chevronUp: ChevronUp,
        globe: Globe,
        logOut: LogOut
      }
    }
  },
  methods: {
    open() {
      const { left, top } = this.$el.getBoundingClientRect()
      this.panelStyle = {
        left: `${left}px`,
        bottom: `${window.innerHeight - top + PANEL_OFFSET}px`
      }
      this.isOpen = true
    },
    close() {
      this.isOpen = false
    },
    toggle() {
      if (this.isOpen) this.close()
      else this.open()
    },
    closeAndFocusTrigger() {
      this.close()
      this.$refs.trigger.focus()
    },
    openLanguageModal() {
      this.closeAndFocusTrigger()
      this.isLanguageModalOpen = true
    },
    handleLogout() {
      this.close()
      this.logout()
    }
  }
}
</script>
